/*
 * IndianJewellery.com — Account Wishlist Sync (quiet merge polling v6)
 * Shopify Customer Account API / OAuth PKCE / CAS metafieldsSet.
 * No Admin token, no Cloudflare customer-data storage, no video code changes.
 * Storefront Shopify account authentication is separate from Shopify customer account logout.
 * Removals are NEVER inferred from an empty browser wishlist or a storage reset.
 * Keep unpublished until cross-account/SSO sign-out validation.
 */
(() => {
  'use strict';
  if (window.IJWishlistAccountSync) return;

  const CFG = window.IJWishlistAccountConfig || {};
  const CORE = () => window.IJWishlistSyncCore;
  const BASE = 'https://indianjewellery.com';
  const CALLBACK = BASE + '/pages/ij-wishlist-auth-test';
  const FIELD = {namespace:'ij_wishlist', key:'saved_items', type:'json'};
  const LOCAL = 'wishlist-products';
  const FLOW = 'ij_wishlist_oauth_pending_v2';
  const AUTH = 'ij_wishlist_auth_session_v2';
  const LAST_OWNER = 'ij_wishlist_last_owner_v2';
  const GUEST = 'ij_wishlist_original_guest_v2';
  const LAST_LOCAL_PREFIX = 'ij_wishlist_last_view_v2_';
  const SILENT_CHECK = 'ij_wishlist_silent_check_v2';
  const SSO_VERIFIED = 'ij_wishlist_sso_verified_v3';
  const OWNER_HISTORY = 'ij_wishlist_known_account_v3';
  const STOREFRONT_LOGIN_STATE = 'ij_wishlist_storefront_login_state_v1';
  const REMOVALS = 'ij_wishlist_explicit_removals_v3';
  const SSO_WINDOW_MS = 5 * 60 * 1000;
  const LIMIT_BYTES = 90000;
  const RETRIES = 3;
  const reShopId = /^gid:\/\/shopify\/Customer\/\d+$/;
  const reVarId = /^(?:gid:\/\/shopify\/ProductVariant\/)?(\d{1,20})$/;
  const reHandle = /^[a-z0-9][a-z0-9-]*$/;
  const EXP_MARGIN = 45000;
  // Poll quietly for account changes; local actions stay authoritative while in progress.
  const SILENT_POLL_MS = 5000;
  const watchers = new Set();
  let session = null;
  let endpoints = null;
  let currentOwner = null;
  let running = false;
  let muted = false;
  let syncTimer = null;
  let silentPollTimer = null;
  let localMutationVersion = 0;
  let bar = null;
  let status = null;
  let connectButton = null;
  let disconnectButton = null;
  let currentSnapshot = null;
  let ownerReady = false;
  let syncAgain = false;
  let loginPending = false;
  const callbackPage = location.pathname === '/pages/ij-wishlist-auth-test';
  const wishlistPage = /(?:^|\/)wishlist\/?$/.test(location.pathname);

  // Strictly scoped to the real store: prevents accidental use in a dev shop.
  if (!CFG.clientId || CFG.enabled !== true || location.origin !== BASE) return;

  function localCountStatus() {
    try {
      const count = uniqueValid(readLocal()).length;
      return count + ' item' + (count === 1 ? '' : 's') + ' in your wishlist on this device.';
    } catch {
      return 'Your wishlist is saved on this device.';
    }
  }
  function setStatus(message, isError = false) {
    if (status) {
      status.textContent = CFG.customerSignedIn === true ? localCountStatus() : message;
      status.setAttribute('data-error', String(isError));
    }
    for (const cb of watchers) try { cb({message, isError, owner: currentOwner}); } catch {}
  }
  function requestReconfirmation(message) {
    setStatus(message);
    if (connectButton && CFG.customerSignedIn !== true) {
      connectButton.textContent = 'Sign in';
      connectButton.style.display = '';
    }
  }
  
  function showSyncPendingStatus() {
    setStatus('Wishlist updated on this device. Account sync is waiting for session verification.');
    if (CFG.customerSignedIn === true && connectButton) {
      connectButton.style.display = 'none';
    }
  }  
  
  function fail(error) {
    const detail = String(error?.message || error || '');
    const waitingForSession =
      /Shopify sign-in needs reconfirmation before saving/i.test(detail);
    const needsSignIn = error?.status === 401 ||
      /\bHTTP 401\b|unauthori[sz]ed|access token|sign-in is required/i.test(detail);
    const message = needsSignIn
      ? 'or create an account to save your wishlist.'
      : 'Your wishlist is saved on this device. Account syncing is temporarily unavailable.';

    if (waitingForSession && CFG.customerSignedIn === true) {
      showSyncPendingStatus();
    } else if (needsSignIn) {
      requestReconfirmation(message);
      if (connectButton) connectButton.textContent = 'Sign in';
    } else {
      setStatus(message, true);
    }

    if (callbackPage && !status) {
      const note = document.createElement('div');
      note.style.cssText = 'max-width:640px;margin:40px auto;padding:20px;border:1px solid #ddd;border-radius:10px';
      const title = document.createElement('strong');
      title.textContent = 'Wishlist account connection';
      const msg = document.createElement('p');
      msg.textContent = message;
      const back = document.createElement('a');
      back.href = '/pages/wishlist'; back.textContent = 'Return to Wishlist';
      note.append(title,msg,back); document.body.append(note);
    }
    console.warn('[IJ Wishlist Account]', error);
  }
  function parseJSON(text, fallback) {
    try { return JSON.parse(text); } catch { return fallback; }
  }
  function itemId(value) {
    const match = String(value ?? '').match(reVarId);
    return match ? match[1] : null;
  }
  function uniqueValid(items) {
    const map = new Map();
    for (const entry of items || []) {
      if (!entry || typeof entry !== 'object' || Array.isArray(entry)) continue;
      const id = itemId(entry.variant_id ?? entry.variantId);
      if (!id || !reHandle.test(String(entry.handle || ''))) continue;
      if (!map.has(id)) map.set(id, {
        handle: entry.handle, variant_id: id,
        variant_name: typeof entry.variant_name === 'string' ? entry.variant_name : '',
        variant_price: typeof entry.variant_price === 'string' ? entry.variant_price : null,
        added_at: typeof entry.added_at === 'string' ? entry.added_at : null,
      });
    }
    return [...map.values()];
  }
  function readLocal() {
    const raw = localStorage.getItem(LOCAL);
    const parsed = raw == null ? [] : parseJSON(raw, null);
    if (!Array.isArray(parsed)) throw new Error('Browser wishlist is invalid; nothing has been changed.');
    return parsed;
  }
  function writeLocal(items) {
    // Preserve localStorage schema understood by existing global.js and video page.
    const before = readLocal();
    const beforeUnique = uniqueValid(before);
    const next = uniqueValid(items);
    // Metadata/order differences are not wishlist changes. Avoid rewriting the
    // cache and repainting hearts/cards unless the variant membership changed.
    const beforeIds = beforeUnique.map(item => item.variant_id).sort();
    const nextIds = next.map(item => item.variant_id).sort();
    const sameMembership = beforeIds.length === nextIds.length &&
      beforeIds.every((id, index) => id === nextIds[index]);
    if (sameMembership && before.length === beforeUnique.length) return false;
    localStorage.setItem(LOCAL, JSON.stringify(next));
    muted = true;
    try {
      document.dispatchEvent(new Event('wishlistUpdated'));
      try { window.globalWishlistInit?.(); } catch {}
    } finally { setTimeout(() => { muted = false; }, 120); }
    // The wishlist page reconciles account changes in place; sync must not
    // reload the whole page.
    return true;
  }
  function copy(value) { return JSON.parse(JSON.stringify(value)); }
  function readState(key, fallback) {
    const v = localStorage.getItem(key);
    if (v === null) return fallback;
    const parsed = parseJSON(v, null);
    if (parsed === null) throw new Error('Account wishlist state cannot be read safely.');
    return parsed;
  }
  const lastViewKey = id => LAST_LOCAL_PREFIX + id.match(/\d+$/)[0];
  function encode(bytes) {
    let s = ''; for (const n of bytes) s += String.fromCharCode(n);
    return btoa(s).replace(/\+/g,'-').replace(/\//g,'_').replace(/=+$/,'');
  }
  function random(size=32) { const a = new Uint8Array(size); crypto.getRandomValues(a); return encode(a); }
  async function challenge(verifier) {
    return encode(new Uint8Array(await crypto.subtle.digest('SHA-256',new TextEncoder().encode(verifier))));
  }
  async function request(url, opts={}) {
    const response = await fetch(url, {cache:'no-store', credentials:'omit', ...opts});
    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
      const reason = String(body.error_description || body.error || body.message || `HTTP ${response.status}`);
      const error = new Error(reason.slice(0,240));
      error.status = response.status;
      throw error;
    }
    return body;
  }
  async function discovery() {
    if (endpoints) return endpoints;
    const [openid, account] = await Promise.all([
      request(BASE + '/.well-known/openid-configuration'),
      request(BASE + '/.well-known/customer-account-api'),
    ]);
    const urls = [openid.authorization_endpoint, openid.token_endpoint, account.graphql_api];
    if (urls.some(u => !u || new URL(u).protocol !== 'https:')) throw new Error('Shopify authentication endpoints are unavailable.');
    endpoints = {openid, account};
    return endpoints;
  }
  const READ = `query IJWishlistSyncRead { customer { id metafield(namespace:"ij_wishlist", key:"saved_items") { type value compareDigest } } }`;
  const WRITE = `mutation IJWishlistSyncWrite($metafields:[MetafieldsSetInput!]!) { metafieldsSet(metafields:$metafields) { metafields {namespace key value compareDigest} userErrors {field code message} } }`;
  async function graphql(query, variables) {
    const {account} = await discovery();
    if (!session?.accessToken) throw new Error('Sign-in is required to access your account wishlist.');
    const data = await request(account.graphql_api, {
      method:'POST',
      headers:{'Content-Type':'application/json', Authorization:session.accessToken},
      body:JSON.stringify({query, ...(variables ? {variables} : {})}),
    });
    if (data.errors?.length) throw new Error(data.errors.map(x => x.message).join('; '));
    if (!data.data) throw new Error('Shopify did not return customer account data.');
    return data.data;
  }
  async function snapshot() {
    const {customer} = await graphql(READ);
    if (!reShopId.test(String(customer?.id || ''))) throw new Error('Shopify could not confirm this customer.');
    let document = {version:1,items:[]};
    if (customer.metafield) {
      if (customer.metafield.type !== 'json') throw new Error('The account wishlist is not JSON.');
      document = CORE().parseSharedDocument(customer.metafield.value);
    }
    return {owner:customer.id, digest:customer.metafield?.compareDigest ?? null, document};
  }
  function saveAuth() {
    sessionStorage.setItem(AUTH, JSON.stringify(session));
  }
  function restoreAuth() {
    const parsed = parseJSON(sessionStorage.getItem(AUTH) || '', null);
    if (parsed?.accessToken && parsed?.expiresAt > Date.now() + EXP_MARGIN) {
      session = parsed;
      return true;
    }
    sessionStorage.removeItem(AUTH);
    session = null;
    return false;
  }
  function clearAuth() {
    session = null;
    sessionStorage.removeItem(AUTH);
    currentOwner = null;
    ownerReady = false;
    currentSnapshot = null;
    clearTimeout(syncTimer);
    sessionStorage.removeItem(REMOVALS);
  }
  function restoreGuestView() {
    // Do not treat the absence of account credentials as a wishlist removal.
    const owner = localStorage.getItem(LAST_OWNER);
    if (!owner) return;
    const guest = readState(GUEST, null);
    if (!Array.isArray(guest)) {
      throw new Error('Guest wishlist backup unavailable; no account or local list has been changed.');
    }
    writeLocal(guest, {reload:false});
    localStorage.removeItem(LAST_OWNER);
  }
  function readRemovalQueue(owner) {
    const value = parseJSON(sessionStorage.getItem(REMOVALS) || '', null);
    return value?.owner === owner && Array.isArray(value.ids)
      ? new Set(value.ids.map(itemId).filter(Boolean)) : new Set();
  }
  function storeRemovalQueue(owner, ids) {
    sessionStorage.setItem(REMOVALS, JSON.stringify({owner, ids:[...ids]}));
  }
  function clearVerifiedSSO() { sessionStorage.removeItem(SSO_VERIFIED); }
  function ssoFresh() {
    const t = Number(sessionStorage.getItem(SSO_VERIFIED));
    const now = Date.now();
    const age = now - t;
    const tokenWindow = Number(session?.expiresAt || 0) - t - EXP_MARGIN;
    const allowedAge = Math.min(SSO_WINDOW_MS, tokenWindow);
    return Number.isFinite(t) && t > 0 && age >= 0 && allowedAge > 0 && age < allowedAge;
  }
  function disconnect({blockSilent=true}={}) {
    clearAuth();
    clearVerifiedSSO();
    if (blockSilent) sessionStorage.setItem(SILENT_CHECK, 'manual');
    restoreGuestView();
    if (connectButton) connectButton.style.display = '';
    if (disconnectButton) disconnectButton.style.display = 'none';
    setStatus('Account wishlist disconnected from this browser. Your Shopify records remain saved.');
  }
  async function beginLogin({silent=false}={}) {
    if (loginPending) return;
    loginPending = true;
    try {
    const {openid} = await discovery();
    const state = random(32), verifier = random(48);
    const flow = {state, verifier, startedAt:Date.now(), silent, returnTo: callbackPage ? '/pages/wishlist' : location.pathname + location.search};
    sessionStorage.setItem(FLOW, JSON.stringify(flow));
    const url = new URL(openid.authorization_endpoint);
    for (const [key,value] of Object.entries({
      client_id:CFG.clientId, response_type:'code', scope:'openid email customer-account-api:full',
      redirect_uri:CALLBACK, state, code_challenge:await challenge(verifier), code_challenge_method:'S256'
    })) url.searchParams.set(key,value);
    if (silent) url.searchParams.set('prompt','none');
    location.assign(url.toString());
    } catch (err) { loginPending = false; throw err; }
  }
  async function finishLogin() {
    const params = new URLSearchParams(location.search);
    if (!params.has('code') && !params.has('error')) return false;
    if (!callbackPage) throw new Error('Unexpected Shopify authorization callback location.');
    // Clear authorization code immediately from URL and browser history.
    history.replaceState(null,'',CALLBACK);
    const flow = parseJSON(sessionStorage.getItem(FLOW) || '',null);
    sessionStorage.removeItem(FLOW);
    if (!flow || !flow.state || flow.state !== params.get('state') ||
        !flow.verifier || Date.now()-flow.startedAt > 600000 || Date.now()<flow.startedAt) {
      throw new Error('Shopify sign-in security verification failed.');
    }
    if (params.has('error')) {
      if (flow.silent) {
        clearAuth();
        clearVerifiedSSO();
        restoreGuestView();
        sessionStorage.setItem(SILENT_CHECK, 'no-session:' + Date.now());
        setStatus('or create an account to save your wishlist.');
        location.replace(flow.returnTo.startsWith('/') ? flow.returnTo : '/pages/wishlist');
        return true;
      }
      throw new Error('Shopify sign-in returned: ' + params.get('error'));
    }
    if (!params.get('code')) throw new Error('Shopify did not return an authorization code.');
    const {openid} = await discovery();
    const data = await request(openid.token_endpoint, {
      method:'POST', headers:{'Content-Type':'application/x-www-form-urlencoded'},
      body:new URLSearchParams({grant_type:'authorization_code', client_id:CFG.clientId,
        redirect_uri:CALLBACK, code:params.get('code'),code_verifier:flow.verifier}).toString()
    });
    if (!data.access_token) throw new Error('No Shopify customer access token was returned.');
    // Access tokens are confined to this browser tab's sessionStorage (not localStorage).
    // No client secret or Admin API key is stored in the theme.
    session = {accessToken:data.access_token, expiresAt:Date.now()+Math.max(60,Number(data.expires_in)||300)*1000};
    saveAuth();
    sessionStorage.setItem(SSO_VERIFIED, String(Date.now()));
    sessionStorage.setItem(SILENT_CHECK, 'completed');
    const after = flow.returnTo.startsWith('/') && !flow.returnTo.startsWith('//') ? flow.returnTo : '/pages/wishlist';
    location.replace(after);
    return true;
  }
  function recordsFromServer(items) {
    return uniqueValid((items||[]).filter(x => x && x.test_only !== true).map(x => ({
      handle:x.handle, variant_id:x.variant_id, variant_name:x.variant_name,
      added_at:x.added_at, variant_price:null
    })));
  }
  async function syncOnce(options = {}) {
    const silent = options.silent === true;
    const applyRemoteToLocal = options.applyRemoteToLocal === true;
    // The account token may outlive storefront sign-out. Never sync while Liquid says guest.
    if (CFG.customerSignedIn !== true) return {skipped:'signed-out'};
    if (running) { syncAgain = true; return; }
    if (!CORE()) throw new Error('Wishlist synchronization core is missing.');
    if (!session?.accessToken || session.expiresAt <= Date.now() + EXP_MARGIN) {
      disconnect({blockSilent:false});
      throw new Error('The wishlist account connection expired. Connect again to restore saved designs.');
    }
    // No silent authentication verification = NO account writes. A valid API token
    // alone does not prove the customer is still signed into Shopify's account UI.
if (!ssoFresh()) {
  if (silent) return { skipped: 'session-verification' };
  setStatus('Confirming your current Shopify account before syncing…');
  await beginLogin({silent:true});
  return;
}
    running = true;
    const localVersionAtStart = localMutationVersion;
    try {
      if (!silent) setStatus('Updating your saved wishlist…');
      let first = await snapshot();
      if (currentOwner && currentOwner !== first.owner) {
        disconnect();
        throw new Error('Customer account changed. Reconnect to load the correct wishlist.');
      }
      currentOwner = first.owner;
      const previousOwner = localStorage.getItem(LAST_OWNER);
      const knownOwner = localStorage.getItem(OWNER_HISTORY);
      if (previousOwner && previousOwner !== first.owner) {
        // Account switch: restore the original guest list but NEVER auto-import
        // it into the newly connected account.
        restoreGuestView();
        sessionStorage.removeItem(REMOVALS);
      }
      const ownerChanged = (previousOwner && previousOwner !== first.owner) ||
        (knownOwner && knownOwner !== first.owner);
      const savedBaseline = readState(lastViewKey(first.owner), null);
      if (savedBaseline !== null && !Array.isArray(savedBaseline)) {
        throw new Error('The account reference snapshot is invalid; no data was changed.');
      }
      if (!localStorage.getItem(GUEST)) {
        localStorage.setItem(GUEST, JSON.stringify(readLocal()));
      }
      const originalLocal = readLocal();
      const local = uniqueValid(originalLocal);
      if (local.length !== originalLocal.length) {
        throw new Error('Some browser wishlist entries require review; no data was overwritten.');
      }
      // Track the local snapshot used for this pass. A new heart click can
      // happen while Shopify is responding; preserve those later additions at
      // commit so this older snapshot cannot replace them in localStorage.
      const localKeysAtStart = new Set(local.map(item => item.variant_id));
      const baseline = uniqueValid(savedBaseline || []);
      const baselineKeys = new Set(baseline.map(x => x.variant_id));
      const locallyNew = ownerChanged ? [] : local.filter(x => !baselineKeys.has(x.variant_id));
      // The only allowed deletes are for explicit heart clicks captured while
      // an authenticated, fully hydrated account was active.
      const queuedRemovals = readRemovalQueue(first.owner);
      const remotelyConfirmedRemovals = new Set();
      let writtenDocument = null;
      let changed = false;
      for (let attempt = 0; attempt < RETRIES; attempt++) {
        if (attempt) first = await snapshot();
        if (first.owner !== currentOwner) throw new Error('Customer identity changed during synchronization.');
        const remote = first.document;
        const remoteKeys = new Set(remote.items.map(x => itemId(x.variant_id)).filter(Boolean));
        // Keep a local removal marker until a later account READ confirms the
        // item is absent. A mutation acknowledgement alone is not enough: an
        // immediate Shopify read can briefly return the pre-write snapshot.
        for (const id of [...queuedRemovals]) {
          if (!remoteKeys.has(id)) {
            queuedRemovals.delete(id);
            remotelyConfirmedRemovals.add(id);
          }
        }
        const additions = locallyNew.filter(x => !remoteKeys.has(x.variant_id));
        const plan = await CORE().previewMerge({
          remoteDocument:remote, guestItems:additions, origin:location.origin
        });
        if (plan.problems.length) throw new Error(plan.problems.join(' '));
        const saved = remote.items.filter(x => !queuedRemovals.has(itemId(x.variant_id)));
        const next = {...remote, items:[...saved, ...plan.proposedDocument.items.slice(remote.items.length)]};
        const value = JSON.stringify(next);
        if (new TextEncoder().encode(value).length > LIMIT_BYTES) {
          throw new Error('Wishlist too large for this storage field. Nothing was removed.');
        }
        changed = JSON.stringify(remote) !== value;
        if (!changed) { writtenDocument = remote; break; }
        // Refuse writes if sign-in verification has aged out while we were computing.
        if (!ssoFresh()) throw new Error('Shopify sign-in needs reconfirmation before saving.');
        try {
          const data = await graphql(WRITE, {metafields:[{
            ownerId:first.owner, ...FIELD, value, compareDigest:first.digest
          }]});
          const result = data.metafieldsSet;
          if (!result || result.userErrors?.length) {
            throw new Error(result?.userErrors?.map(x=>x.message).join('; ') || 'Shopify did not confirm update');
          }
          if (!result.metafields?.some(x=>x.namespace===FIELD.namespace && x.key===FIELD.key)) {
            throw new Error('Shopify did not acknowledge the wishlist metafield update.');
          }
          // Mutation acknowledgement is authoritative for THIS write; immediate
          // follow-up queries can briefly return a stale count.
          writtenDocument = next;
          break;
        } catch (err) {
          if (/compare|digest|stale|conflict/i.test(String(err.message)) && attempt < RETRIES-1) continue;
          throw err;
        }
      }
      if (!writtenDocument) throw new Error('Shopify update did not complete.');
      // A heart can be removed while the Shopify request above is in flight.
      // Keep those newer explicit removals out of the local reconciliation
      // even though they were not part of this request's initial queue snapshot.
      const pendingAtCommit = readRemovalQueue(first.owner);
      const allPendingRemovals = new Set([...queuedRemovals, ...pendingAtCommit]);
      const accountVisible = recordsFromServer(writtenDocument.items)
        .filter(item => !allPendingRemovals.has(item.variant_id));
      if (writtenDocument.items.some(x => x.test_only !== true && itemId(x.variant_id) &&
          !reHandle.test(String(x.handle || '')))) {
        throw new Error('An account record has no product handle. Browser wishlist unchanged.');
      }
      // Server is authoritative for known saved variants. Only truly new local
      // additions survive; stale copies of remotely removed items are not restored.
      // Also retain additions made after this sync read its initial local snapshot.
      const latestLocal = uniqueValid(readLocal());
      const lateLocalAdditions = latestLocal.filter(item =>
        !localKeysAtStart.has(item.variant_id) &&
        !accountVisible.some(saved => saved.variant_id === item.variant_id) &&
        !allPendingRemovals.has(item.variant_id)
      );
      const canonicalById = new Map(accountVisible.map(item => [item.variant_id, item]));
      for (const item of [...locallyNew, ...lateLocalAdditions]) {
        if (!canonicalById.has(item.variant_id) && !allPendingRemovals.has(item.variant_id)) {
          canonicalById.set(item.variant_id, item);
        }
      }
      const canonical = [...canonicalById.values()];
      localStorage.setItem(LAST_OWNER, first.owner);
      localStorage.setItem(OWNER_HISTORY, first.owner);
      // Account responses may update the local cache only at a quiet checkpoint.
      // The version guard skips this write if the shopper changed the wishlist
      // while the request was in flight. Local-action saves never repaint from
      // their own account response.
      if (applyRemoteToLocal && localVersionAtStart === localMutationVersion) {
        localStorage.setItem(
          lastViewKey(first.owner),
          JSON.stringify(recordsFromServer(writtenDocument.items))
        );
        ownerReady = true;
        // The wishlist section already renders from wishlistUpdated. This emits
        // one local event only if the checkpoint actually changed the cache.
        writeLocal(canonical);
      }
      currentSnapshot = {owner:first.owner, document:writtenDocument};
      // Keep queued removals after a successful write. Clear only removals
      // confirmed absent by an account READ, while preserving clicks (including
      // re-adds that cancel a removal) made during this request.
      const newerQueue = readRemovalQueue(first.owner);
      for (const id of remotelyConfirmedRemovals) newerQueue.delete(id);
      storeRemovalQueue(first.owner, newerQueue);
      if (!silent) {
  setStatus(canonical.length + ' item' + (canonical.length === 1 ? '' : 's') + ' saved to your account.');
}
      return {owner:first.owner, count:canonical.length, changed};
    } finally {
      running = false;
      const needsAnotherRun = syncAgain;
      syncAgain = false;
      if (needsAnotherRun) scheduleSync(350);
    }
  }

function scheduleSync(delay = 150, options = {}) {
  // Syncs caused by local actions or polling are quiet unless explicitly asked otherwise.
  const silent = options.silent !== false;

  if (muted || !session || CFG.customerSignedIn !== true) return;

  if (!ssoFresh()) return;

  if (running) {
    // Only a real local change needs a follow-up; a poll can wait for its next interval.
    if (options.localChange === true) syncAgain = true;
    return;
  }

  clearTimeout(syncTimer);
  syncTimer = setTimeout(() => {
    syncOnce({silent, applyRemoteToLocal:options.applyRemoteToLocal === true}).catch(error => {
      if (silent) {
        console.warn('[IJ Wishlist Account] Background sync deferred.', error);
      } else {
        fail(error);
      }
    });
  }, delay);
}

function startSilentPolling() {
  if (silentPollTimer) return;

  silentPollTimer = window.setInterval(() => {
    if (
      document.visibilityState !== 'visible' ||
      CFG.customerSignedIn !== true ||
      !session?.accessToken ||
      session.expiresAt <= Date.now() + EXP_MARGIN ||
      !ssoFresh() ||
      running
    ) {
      return;
    }

    // A poll may save local changes to the account, but never writes the
    // account snapshot into the live local cache or repaints the current page.
    scheduleSync(0, {silent:true, applyRemoteToLocal:false});
  }, SILENT_POLL_MS);
}

  function captureExplicitHeartClick(event) {
    // Only a real customer click while Shopify confirms sign-in can create or
    // cancel a removal intent. App-driven storage writes never create tombstones.
    if (!event.isTrusted || CFG.customerSignedIn !== true) return;
    const btn = event.target?.closest?.('[button-wishlist], button[data-ijss-wish], .wishlist-btn');
    if (!btn) return;
    const variantId = itemId(btn.dataset.variantId || btn.getAttribute('data-variant-id'));
    const handle = btn.dataset.productHandle || btn.dataset.handle;
    if (!variantId || !reHandle.test(String(handle || ''))) return;

    const before = uniqueValid(readLocal());
    const wasPresent = before.some(x => x.variant_id === variantId && x.handle === handle);
    const customerId = String(CFG.customerId || '').match(/\d+$/)?.[0] || '';
    // Liquid's customer ID is authoritative before the Customer Account API
    // token finishes its silent verification on this page.
    const owner = customerId ? `gid://shopify/Customer/${customerId}`
      : (currentOwner || localStorage.getItem(LAST_OWNER));
    if (!reShopId.test(String(owner || ''))) return;

    const pending = readRemovalQueue(owner);
    const wasAlreadyPending = pending.has(variantId);
    if (wasPresent) pending.add(variantId);
    else pending.delete(variantId); // a re-add cancels an earlier pending removal
    storeRemovalQueue(owner, pending);
    if (!wasPresent) return;

    // Confirm that the theme's handler completed the local removal. If not,
    // undo only the intent added by this click. Keep it queued if auth is not
    // ready yet; the next verified sync will safely apply it to this customer.
    setTimeout(() => {
      if (currentOwner && currentOwner !== owner) return;
      const after = uniqueValid(readLocal());
      if (after.some(x => x.variant_id === variantId)) {
        if (!wasAlreadyPending) {
          const stillPending = readRemovalQueue(owner);
          stillPending.delete(variantId);
          storeRemovalQueue(owner, stillPending);
        }
        return;
      }
      if (ssoFresh()) scheduleSync(0, {localChange:true});
    }, 0);
  }

  function createBar() {
    if (!wishlistPage || bar || !document.querySelector('#ij-wishlist-video-grid')) return;
    bar = document.createElement('div');
    bar.id = 'ij-wishlist-account-controls';
    bar.style.cssText = 'max-width:900px;margin:16px auto 20px;padding:12px 18px;border:1px solid #dfd7c7;border-radius:10px;font:inherit;display:flex;flex-wrap:wrap;align-items:center;gap:12px;';
    status = document.createElement('span');
    status.setAttribute('role','status'); status.setAttribute('aria-live','polite');
    status.style.cssText = 'flex:1 1 240px;font-size:14px;';
    if (CFG.customerSignedIn === true) {
      // Keep the visible account bar useful, but make its count come only from
      // this device's local cache. Account responses never set this count.
      status.textContent = localCountStatus();
    } else {
      status.textContent = 'or create an account to save your wishlist.';
    }
    // Shopify SSO handles returning signed-in customers automatically.
    // This visible sign-in action also gives guests a clear way to save across
    // devices; it is hidden as soon as the account session is confirmed.
    connectButton = document.createElement('button');
    connectButton.type = 'button';
    connectButton.textContent = 'Sign in';
    connectButton.style.cssText = 'cursor:pointer;background:#fff;border:1px solid #666;border-radius:7px;padding:10px 13px;' +
      (CFG.customerSignedIn === true ? ';display:none;' : '');
    connectButton.addEventListener('click', () => {
  if (CFG.customerSignedIn !== true && CFG.storefrontLoginUrl) {
    location.assign(CFG.storefrontLoginUrl);
    return;
  }
  beginLogin().catch(fail);
});
    bar.append(connectButton, status);
    document.querySelector('#ij-wishlist-video-grid').before(bar);
  }
  async function bootstrap() {
    const params = new URLSearchParams(location.search);
    if (callbackPage && (params.has('code') || params.has('error'))) {
      await finishLogin();
      return;
    }
    // On a new browser tab without an authentication session, remove the
    // account-only browser cache from view. The original guest list is kept.
    const hasSession = restoreAuth();
    const signedInNow = CFG.customerSignedIn === true;
    const previousStorefrontState =
      sessionStorage.getItem(STOREFRONT_LOGIN_STATE) ||
      localStorage.getItem(STOREFRONT_LOGIN_STATE);

    const justSignedInOnThisTab =
      signedInNow && previousStorefrontState === 'signed-out';

    const currentStorefrontState =
      signedInNow ? 'signed-in' : 'signed-out';

    sessionStorage.setItem(STOREFRONT_LOGIN_STATE, currentStorefrontState);
    localStorage.setItem(STOREFRONT_LOGIN_STATE, currentStorefrontState);
    if (!signedInNow || justSignedInOnThisTab) {
      sessionStorage.removeItem(SILENT_CHECK);
    }

    // Keep the current local wishlist when the customer signs out.
    // Clear account verification and queued signed-in removals.
    if (!signedInNow) {
      clearVerifiedSSO();
      sessionStorage.removeItem(REMOVALS);
    }
    createBar();
    // One silent SSO verification per recently opened Wishlist visit. The
    // browser token alone is not a reliable indicator of Shopify sign-in state.
    // When Shopify sign-out is detected, finishLogin() restores guest items.
    const ssoStatus = sessionStorage.getItem(SILENT_CHECK) || '';
    const lastNoSession = ssoStatus.startsWith('no-session:')
      ? Number(ssoStatus.slice('no-session:'.length)) : 0;
    const recentNoSession = lastNoSession > 0 && Date.now() - lastNoSession < 60000;
    // Reconfirm when this tab has no usable account session or its recent SSO
    // verification has expired. A fresh, already-verified visit stays in place.
    const shouldAutoConnectAfterMainSignIn = justSignedInOnThisTab;
    // Verify a signed-in customer on any storefront page, not only Wishlist.
    // This lets the account session initialize before the customer visits Wishlist.
    const shouldRecheckAccount = ssoStatus !== 'completed' && !recentNoSession &&
      (!hasSession || !ssoFresh());
    if (signedInNow && CFG.silentAccountCheck === true &&
        (shouldAutoConnectAfterMainSignIn || shouldRecheckAccount)) {
      sessionStorage.setItem(SILENT_CHECK, 'attempted');
      await beginLogin({silent:true});
      return;
    }
    if (ssoStatus === 'completed') sessionStorage.removeItem(SILENT_CHECK);
    if (hasSession && CFG.customerSignedIn === true) {
      if (wishlistPage && disconnectButton) disconnectButton.style.display = '';
        // Reconcile on page entry when this tab already has a recent Shopify
      // sign-in verification. The wishlist poll then checks quietly while
      // browsing; cart sync retains its own authentication flow.
      if (ssoFresh()) {
        if (connectButton) connectButton.style.display = 'none';
        // Adopt the current account snapshot on page entry. The recurring
        // background checkpoint handles later cross-device changes quietly.
        syncOnce({silent:true, applyRemoteToLocal:true}).catch(error => {
          console.warn('[IJ Wishlist Account] Page checkpoint deferred.', error);
        });
      } else if (wishlistPage) {
        requestReconfirmation('Your wishlist is saved on this device. Sign in again to sync it across devices.');
      }
    }
    // Use capture phase to observe genuine customer heart clicks before the
    // theme mutates the wishlist. Only those can cause server-side deletions.
    document.addEventListener('click', captureExplicitHeartClick, true);
    // global.js emits both legacy names on document and window. Listen to one
    // canonical event only so one heart click schedules one account sync.
    document.addEventListener('wishlistUpdated', () => {
      if (!muted) localMutationVersion++;
      if (status && CFG.customerSignedIn === true) {
        status.textContent = localCountStatus();
      }
      scheduleSync(150, {localChange:true});
    });
    window.addEventListener('storage', e => {
      if (e.key === LOCAL) {
        localMutationVersion++;
        scheduleSync(150, {localChange:true});
      }
    });
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && session && ssoFresh()) {
        // Resume account-only sync when the tab returns; leave this page's
        // rendered wishlist and local cache untouched.
        scheduleSync(250, {applyRemoteToLocal:false});
      }
    });

    if (hasSession && signedInNow && ssoFresh()) {
      startSilentPolling();
    }
  }
  window.IJWishlistAccountSync = Object.freeze({
  connect:() => beginLogin(),
  connectSilently:() => beginLogin({silent:true}),
  graphql:(query, variables) => graphql(query, variables),
  sync:() => syncOnce({applyRemoteToLocal:true}),
  disconnect:() => disconnect(),
  getStatus:() => ({
    authenticated:!!session,
    owner:currentOwner,
    syncing:running,
    ssoVerified:ssoFresh(),
    expiresAt:session?.expiresAt || 0
  }),
  subscribe:fn => {if (typeof fn === 'function') watchers.add(fn); return () => watchers.delete(fn);}
});

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => bootstrap().catch(fail), {once:true});
  else bootstrap().catch(fail);
})();
